import { useState } from 'react';
import { Eye, Send, FileText } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useSTPSubmissions, useSTPDashboard, useSubmitSTPToATO, usePrepareSTPSubmission } from '@/hooks/useSTP';
import { STPSubmission, STP_STATUSES } from '@/types';
import { formatDate } from '@/lib/utils';
import { Link } from 'react-router-dom';

function statusBadge(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    READY: 'success',
    VALIDATION_FAILED: 'destructive',
    SUBMITTED: 'info',
    ACCEPTED: 'success',
    REJECTED: 'destructive',
  };
  const config = STP_STATUSES.find((s) => s.value === status);
  return <Badge variant={variants[status] || 'secondary'}>{config?.label || status}</Badge>;
}

export function STPSubmissionsPage() {
  const [statusFilter, setStatusFilter] = useState('all');
  const { toast } = useToast();
  const { data: dashboard } = useSTPDashboard();
  const { data, isLoading } = useSTPSubmissions({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const submitToATO = useSubmitSTPToATO();
  const prepare = usePrepareSTPSubmission();

  const submissions: STPSubmission[] = (data as any)?.data || [];

  const handleSubmit = async (id: string) => {
    try {
      const result = await submitToATO.mutateAsync(id);
      if (result.lodged) {
        toast({ title: 'Lodged', description: 'Submission lodged with the ATO', variant: 'success' });
      } else {
        toast({
          title: 'Validated, not lodged',
          description: result.message || 'SBR2 credentials required to lodge',
          variant: 'warning' as any,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submit failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const handlePrepare = async (payRunId: string) => {
    try {
      const result = await prepare.mutateAsync(payRunId);
      const errors = result.issues.filter((i) => i.severity === 'ERROR').length;
      toast({
        title: result.status === 'READY' ? 'Ready to lodge' : 'Validation failed',
        description: errors ? `${errors} error(s) must be resolved` : 'No errors found',
        variant: result.status === 'READY' ? 'success' : 'destructive',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Prepare failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const columns: Column<STPSubmission>[] = [
    {
      key: 'payRun',
      header: 'Pay Run',
      cell: (s) => (
        <div>
          <div className="font-medium">
            {s.payRun ? `${formatDate(s.payRun.payPeriodStart)} — ${formatDate(s.payRun.payPeriodEnd)}` : s.payRunId.slice(0, 8)}
          </div>
          {s.payRun && (
            <div className="text-xs text-muted-foreground">Paid {formatDate(s.payRun.paymentDate)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'submissionType',
      header: 'Type',
      cell: (s) => s.submissionType.replace(/_/g, ' '),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (s) => statusBadge(s.status),
    },
    {
      key: 'atoMessage',
      header: 'Notes',
      cell: (s) => <span className="text-xs text-muted-foreground">{s.atoMessage || '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: (s) => (
        <div className="flex gap-1 justify-end">
          <Link to={`/payroll/stp/${s.id}`}>
            <Button variant="ghost" size="sm">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
          {s.status === 'READY' && (
            <Button variant="outline" size="sm" onClick={() => handleSubmit(s.id)} disabled={submitToATO.isPending}>
              <Send className="h-4 w-4 mr-1" /> Lodge
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Single Touch Payroll" description="STP pay events and ATO lodgement" />

      {/* Dashboard cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="card-elevated">
          <CardContent className="pt-5">
            <div className="text-2xl font-semibold font-mono" data-numbers>
              {dashboard?.payRunsAwaitingSubmission?.length ?? 0}
            </div>
            <div className="text-sm text-muted-foreground">Pay runs awaiting STP submission</div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="pt-5">
            <div className="text-2xl font-semibold font-mono" data-numbers>{dashboard?.byStatus?.READY ?? 0}</div>
            <div className="text-sm text-muted-foreground">Ready to lodge</div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="pt-5">
            <div className="text-2xl font-semibold font-mono" data-numbers>
              {(dashboard?.byStatus?.SUBMITTED ?? 0) + (dashboard?.byStatus?.ACCEPTED ?? 0)}
            </div>
            <div className="text-sm text-muted-foreground">Lodged</div>
          </CardContent>
        </Card>
      </div>

      {/* Awaiting submission */}
      {(dashboard?.payRunsAwaitingSubmission?.length ?? 0) > 0 && (
        <Card className="card-elevated">
          <div className="px-5 py-3 border-b font-semibold text-sm">Awaiting STP submission</div>
          <CardContent className="p-0">
            {dashboard!.payRunsAwaitingSubmission.map((pr) => (
              <div key={pr.id} className="flex items-center justify-between px-5 py-3 border-b last:border-0">
                <div className="text-sm">Pay run paid {formatDate(pr.paymentDate)}</div>
                <Button size="sm" variant="outline" onClick={() => handlePrepare(pr.id)} disabled={prepare.isPending}>
                  <FileText className="h-4 w-4 mr-1" /> Prepare submission
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STP_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        keyExtractor={(row) => row.id}
        data={submissions}
        isLoading={isLoading}
        emptyMessage="No STP submissions yet. Prepare one from a paid pay run."
      />
    </div>
  );
}
